import { useEffect, useRef } from 'react';
import { API_BASE_URL } from '@/lib/api';
import { useAppDispatch } from '@/store';
import { api } from '@/store/api';
import { notificationsApi, useStreamTicketMutation } from './api';

/**
 * The realtime connection.
 *
 * Four things this has to get right, all of which are the difference between a feature
 * that works on a laptop and one that works in an office:
 *
 *  1. **Timers cleared on unmount.** A reconnect timer that survives its component
 *     opens a second connection on every remount, and the count then updates two or
 *     three times per event.
 *  2. **Exponential backoff, capped.** A server restart otherwise produces a reconnect
 *     storm from every open tab at once.
 *  3. **A polling fallback.** Corporate proxies buffer or drop `text/event-stream`
 *     entirely. Thirty-second polling of the count is worse than SSE and far better
 *     than a badge that never changes.
 *  4. **A fresh ticket per connection.** The ticket is single-use, so a reconnect must
 *     mint a new one rather than replay the old.
 */

const BACKOFF_START_MS = 1_000;
const BACKOFF_MAX_MS = 30_000;
const POLL_INTERVAL_MS = 30_000;

export type RealtimeHandlers = {
  onNotificationCreated?: (payload: {
    id: string;
    eventKey: string;
    category: string;
    title: string;
    body: string;
    redirectUrl: string | null;
  }) => void;
};

export function useRealtime(enabled: boolean, handlers?: RealtimeHandlers): void {
  const dispatch = useAppDispatch();
  const [requestTicket] = useStreamTicketMutation();

  // Refs, not state: changing these must never re-render, and the cleanup has to see
  // the current values rather than the ones captured when the effect first ran.
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;
  const sourceRef = useRef<EventSource | null>(null);
  const reconnectRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const backoffRef = useRef(BACKOFF_START_MS);
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;

    cancelledRef.current = false;

    const refreshCount = () => {
      dispatch(notificationsApi.util.invalidateTags(['UnreadCount']));
    };

    const startPolling = () => {
      if (pollRef.current) return;
      pollRef.current = setInterval(refreshCount, POLL_INTERVAL_MS);
    };

    const stopPolling = () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };

    const connect = async () => {
      if (cancelledRef.current) return;

      let ticket: string;
      try {
        const result = await requestTicket().unwrap();
        ticket = result.data.ticket;
      } catch {
        // Could not even get a ticket — the session may be gone. Fall back to polling,
        // which will also fail harmlessly if so.
        startPolling();
        scheduleReconnect();
        return;
      }

      if (cancelledRef.current) return;

      const url = `${API_BASE_URL || ''}/api/v1/notifications/stream?ticket=${encodeURIComponent(ticket)}`;
      const source = new EventSource(url, { withCredentials: true });
      sourceRef.current = source;

      source.addEventListener('open', () => {
        // A successful connection resets the backoff, so a brief blip does not leave
        // the next reconnect 30 seconds away.
        backoffRef.current = BACKOFF_START_MS;
        stopPolling();
      });

      source.addEventListener('notification.created', (event) => {
        dispatch(notificationsApi.util.invalidateTags(['Notification', 'UnreadCount']));
        const payload = parse<{
          id: string;
          eventKey: string;
          category: string;
          title: string;
          body: string;
          redirectUrl: string | null;
        }>(event);
        if (payload) handlersRef.current?.onNotificationCreated?.(payload);
      });

      source.addEventListener('notification.read', () => {
        // Cross-tab sync: reading in one tab clears the badge in the others.
        dispatch(notificationsApi.util.invalidateTags(['Notification', 'UnreadCount']));
      });

      source.addEventListener('unread.count', (event) => {
        const payload = parse<{ unreadCount: number }>(event);
        if (!payload) return;

        try {
          dispatch(
            notificationsApi.util.updateQueryData('unreadCount', undefined, (draft) => {
              draft.data.unreadCount = payload.unreadCount;
            }),
          );
        } catch {
          dispatch(notificationsApi.util.invalidateTags(['UnreadCount']));
        }
      });

      source.addEventListener('session.invalidated', () => {
        // The server has ended this session. Clear the cache rather than letting the
        // user sit on a populated screen that now 401s on every request.
        dispatch(api.util.resetApiState());
      });

      source.addEventListener('error', () => {
        source.close();
        sourceRef.current = null;
        startPolling();
        scheduleReconnect();
      });
    };

    const scheduleReconnect = () => {
      if (cancelledRef.current || reconnectRef.current) return;

      const delay = backoffRef.current;
      backoffRef.current = Math.min(delay * 2, BACKOFF_MAX_MS);

      reconnectRef.current = setTimeout(() => {
        reconnectRef.current = null;
        void connect();
      }, delay);
    };

    void connect();

    return () => {
      cancelledRef.current = true;
      sourceRef.current?.close();
      sourceRef.current = null;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      reconnectRef.current = null;
      stopPolling();
    };
  }, [enabled, dispatch, requestTicket]);
}

function parse<T>(event: Event): T | null {
  const data = (event as MessageEvent<string>).data;
  if (typeof data !== 'string') return null;

  try {
    return JSON.parse(data) as T;
  } catch {
    return null;
  }
}
